/**
 * Design tokens de Velora — copia en JS de hrbf_freezer/src/ui/theme/tokens.ts.
 *
 * La fuente de verdad es freezer: si allí cambia un valor, se copia aquí tal cual.
 * No añadir colores que no existan en freezer.
 *
 * Dos capas:
 *   1. primitives — escala bruta por matiz (no usar directamente en componentes)
 *   2. tokens     — semánticos con nombre de propósito (lo que usan los componentes)
 */

// ─────────────────────────────────────────────────────────────────────────────
// CAPA 1 — PRIMITIVOS
// ─────────────────────────────────────────────────────────────────────────────

export const primitives = {
  white: '#FFFFFF',
  black: '#000000',

  /**
   * Escala de colores de marca calculada con MUI lighten/darken.
   * Estos son los valores fijos que antes se computaban en runtime con
   * lighten(PRIMARY, 0.5) etc. en GlobalTheme.jsx.
   *
   * Nomenclatura Figma: Colors / Brand / Primary / {Shade}
   */
  brandPrimary: {
    lighter: '#80BADD', // lighten('#0176BC', 0.5) — fondos muy suaves, badges
    lighterHover: '#99C7E3', // lighten(lighter, 0.2)   — tonal button hover
    lighterDisabled: '#BFDCEE', // lighten(lighter, 0.5)   — tonal button disabled
    light: '#4D9FD0', // lighten('#0176BC', 0.3) — hover en tonal buttons
    lightDisabled: '#82BBDE', // lighten(light, 0.3)     — primary button disabled bg
    main: '#0176BC', // BRAND PRIMARY (VELORA_PRIMARY)
    deep: '#015E96', // entre main y dark — texto de la tab activa (SectionTabs) sobre blue.50
    dark: '#005283', // darken('#0176BC', 0.3)  — hover en solid buttons
  },

  brandSecondary: {
    light: '#5F6166', // lighten('#1B1E25', 0.3)
    main: '#1B1E25', // BRAND SECONDARY (VELORA_SECONDARY)
    dark: '#15181D', // darken('#1B1E25', 0.2)
  },

  brandTertiary: {
    light: '#82E6E3', // lighten('#4DDCD8', 0.3)
    main: '#4DDCD8', // BRAND TERTIARY (VELORA_TERTIARY)
    dark: '#3DB0AC', // darken('#4DDCD8', 0.2)
    vivid: '#01E2D4', // teal vivo (gradiente borde InfoTipBanner)
  },

  /**
   * Azul de marca — escala de tintes del legacy COLORS.ts.
   * Se mantiene para retrocompatibilidad con los imports de `COLORS`.
   * Para código nuevo usar brandPrimary.
   *
   * Nomenclatura Figma: Colors / Blue / {Scale}
   */
  blue: {
    50: '#E6F3FC', // BLUE_100 (legacy)
    100: '#B8DFF5', // BLUE_200 (legacy)
    200: '#E6F1F8', // BLUE_300 (legacy)
    300: '#0084C7', // BLUE_400 (legacy)
    400: '#0176BC', // BLUE_500 (legacy) = brandPrimary.main
    500: '#0168A6', // BLUE_600 (legacy)
    600: '#0369a1', // BLUE_700 (legacy)
  },

  // Azul genérico Tailwind — no es de marca, usar con cuidado
  blueGeneric: {
    500: '#3B82F6',
  },

  /**
   * Cyan / sky — colores de AI assistant y acciones auxiliares.
   * No confundir con el azul de marca.
   *
   * Nomenclatura Figma: Colors / Cyan / {Scale}
   */
  cyan: {
    300: '#22d3ee', // cyan-400 Tailwind
    400: '#06b6d4', // cyan-500 Tailwind
    500: '#0ea5e9', // sky-500  Tailwind — aiPrimary en multiposting
    600: '#0e7490', // cyan-700 Tailwind
  },

  /**
   * Teal — terciario de marca y variantes.
   * Nomenclatura Figma: Colors / Teal / {Scale}
   */
  teal: {
    100: '#DBFCFA', // TEAL_100 (legacy)
    200: '#40E0D0', // TURQUEOISE (legacy)
    300: '#00bfa5', // TEAL_300 (legacy)
    400: '#4DDCD8', // = brandTertiary.main
    500: '#00B8AD', // TEAL_600 (legacy)
    600: '#008F87', // TEAL_700 (legacy)
  },

  /**
   * Verde / emerald.
   * Nomenclatura Figma: Colors / Green / {Scale}
   */
  green: {
    50: '#E1FAEB', // GREEN_50 (legacy)
    100: '#ECFDF5', // GREEN_LIGHT (legacy)
    200: '#CEF1D6', // stepCompletedBg (multiposting)
    300: '#d1fae5', // emerald-100 — success.lighter
    400: '#34d399', // emerald-400 — success.light
    500: '#10B981', // emerald-500 — GREEN (legacy), success.main
    600: '#059669', // emerald-600 — success.dark
    700: '#1bb240', // feedback success (GlobalTheme)
    800: '#29722C', // GREEN_700 (legacy)
    900: '#025832', // GREEN_900 (legacy), stepCompleted
  },

  /**
   * Rojo.
   * Nomenclatura Figma: Colors / Red / {Scale}
   */
  red: {
    50: '#FEF2F2', // FEF2F2 (legacy) — error bg suave
    100: '#fee2e2', // error.lighter
    200: '#fecaca', // error.light
    300: '#FBCECE', // RED_50 (legacy)
    400: '#EF4444', // RED (legacy) — tailwind red-500
    500: '#dc2626', // red-600 — error.main
    600: '#D32F2F', // feedback error (GlobalTheme)
    700: '#C80909', // RED_100 (legacy)
    800: '#B20F0B', // RED_200 (legacy)
    900: '#830B08', // RED_900 (legacy)
  },

  /**
   * Naranja / ámbar.
   * Nomenclatura Figma: Colors / Orange / {Scale}
   */
  orange: {
    50: '#FFF3DB', // ORANGE_50 (legacy)
    100: '#FDF5E7', // ORANGE_100 (legacy)
    300: '#FFA06A', // ORANGE_600 (legacy)
    400: '#FFA500', // naranja genérico (GlobalTheme)
    500: '#DC9501', // ORANGE_500 (legacy)
    600: '#C07F0D', // ORANGE / ORANGE_700 (legacy)
    700: '#FF8C00', // feedback warning (GlobalTheme)
  },

  yellow: {
    100: '#FEF9C3', // YELLOW_LIGHT (legacy)
  },

  /**
   * Rosa.
   * Nomenclatura Figma: Colors / Pink / {Scale}
   */
  pink: {
    50: '#FEF1FD', // PINK_50 (legacy)
    400: '#D946EF', // PINK (legacy) — tailwind pink-500
    600: '#F572E8', // PINK_600 (legacy) — brand pink
  },

  /**
   * Púrpura.
   * Nomenclatura Figma: Colors / Purple / {Scale}
   */
  purple: {
    50: '#F1EDF7', // PURPLE_50 (legacy)
    300: '#C1B3E0', // PURPLE_300 (legacy)
    400: '#A08BCB', // PURPLE_400 (legacy) — brand purple
  },

  /**
   * Neutral — escala de grises canónica para código nuevo.
   * Contiene todas las variantes que están en Figma.
   * Para código legacy ver `legacyGray` abajo.
   *
   * Nomenclatura Figma: Colors / Neutral / {Scale}
   */
  neutral: {
    0: '#FFFFFF', // white
    50: '#FAFAFA', // NEUTRAL_50 (legacy)
    100: '#F5F5F5', // NEUTRAL_100 / GRAY_LIGHTEST (legacy)
    125: '#F4F6FA', // fondo azulado frío (header/wizard background)
    150: '#f3f4f6', // fondo de inputs y chips (GOLD_COLORS background.light)
    200: '#E5E5E5', // NEUTRAL_200 / GRAY_LIGHT (legacy)
    250: '#e5e7eb', // borde sutil (GOLD_COLORS border.light)
    300: '#D4D4D4', // NEUTRAL_300 / stepper connector
    350: '#d1d5db', // borde default (GOLD_COLORS border.main)
    400: '#A1A1A1', // NEUTRAL_400 / GRAY_MEDIUM (legacy)
    450: '#9ca3af', // texto deshabilitado (GOLD_COLORS text.disabled)
    500: '#737373', // NEUTRAL_500 / GRAY_NEUTRAL (legacy)
    525: '#6D6D6D', // texto hint / placeholders secundarios
    550: '#6b7280', // texto terciario (GOLD_COLORS text.tertiary)
    600: '#525252', // NEUTRAL_600 (legacy)
    700: '#404040', // NEUTRAL_700 / BLACK_LIGHT (legacy)
    750: '#374151', // texto secundario (GOLD_COLORS text.secondary)
    800: '#262626', // NEUTRAL_800 (legacy)
    850: '#1e293b', // GRAY_DARK (legacy)
    875: '#16191D', // near-black (form labels)
    900: '#171717', // NEUTRAL_900 (legacy)
    950: '#111827', // texto primario (GOLD_COLORS text.primary)
    975: '#0B0B0B', // near-black
    1000: '#000000', // black
  },

  /**
   * Transparencias / alpha.
   * Nomenclatura Figma: Colors / Alpha / {Name}
   */
  alpha: {
    black10: '#1B242C0A', // BLACK_10 (legacy)
    black14: '#1B242C14', // BLACK_14 (legacy)
    black26: '#1B1E2526', // border DataGrid
    white16: '#FFFFFF29', // blanco al 16% — hover de controles sobre fondo oscuro
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CAPA 2 — TOKENS SEMÁNTICOS
// Nombrados por propósito. Son los que usan los componentes y el diseño de Figma.
// Nomenclatura Figma: Semantic / {Category} / {Role}
// ─────────────────────────────────────────────────────────────────────────────

export const tokens = {
  color: {
    // ── Brand ────────────────────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Brand / *
    brand: {
      primary: primitives.brandPrimary.main, // #0176BC
      primaryLight: primitives.blue[50], // #E6F3FC — fondo suave
      primaryLighter: primitives.brandPrimary.lighter, // #80BADD — hover tonal
      secondary: primitives.brandSecondary.main, // #1B1E25
      tertiary: primitives.brandTertiary.main, // #4DDCD8
      tertiaryVivid: primitives.brandTertiary.vivid, // #01E2D4
      pink: primitives.pink[600], // #F572E8
      orange: primitives.orange[400], // #FFA500
      purple: primitives.purple[400], // #A08BCB
    },

    // ── Interacción de marca ──────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Brand / Interactive / *
    brandInteractive: {
      primaryHover: primitives.brandPrimary.dark, // #005283 — hover botón sólido
      primaryActive: primitives.brandPrimary.dark, // #005283
      primaryTonalHover: primitives.brandPrimary.lighter, // #80BADD — hover botón tonal
      primaryDisabled: primitives.neutral[250], // #e5e7eb
      primaryLightHover: primitives.brandPrimary.light, // #4D9FD0
    },

    // ── Texto ─────────────────────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Text / *
    text: {
      primary: primitives.neutral[950], // #111827
      dark: primitives.neutral[900], // #171717 — texto oscuro
      nearBlack: primitives.neutral[975], // #0B0B0B
      secondary: primitives.neutral[750], // #374151
      tertiary: primitives.neutral[550], // #6b7280
      disabled: primitives.neutral[450], // #9ca3af
      label: primitives.neutral[875], // #16191D — labels de formulario
      hint: primitives.neutral[525], // #6D6D6D — texto secundario / hints
      body: primitives.neutral[500], // #737373 — texto de cuerpo / descripciones
      onDark: primitives.white, // #FFFFFF
      link: primitives.brandPrimary.main, // #0176BC
    },

    // ── Fondos ────────────────────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Background / *
    background: {
      default: primitives.white, // #FFFFFF — superficies principales
      surface: primitives.neutral[50], // #FAFAFA — superficies elevadas
      muted: primitives.neutral[100], // #F5F5F5 — fondos apagados
      cool: primitives.neutral[125], // #F4F6FA — fondo azulado frío (headers, wizards)
      light: primitives.neutral[150], // #f3f4f6 — inputs, chips
      lighter: primitives.neutral[50], // #FAFAFA — sidebar, página
      dark: '#f4f6f8', // fondo oscuro (GlobalTheme)
    },

    // ── Superposiciones ───────────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Overlay / *
    overlay: {
      // Hover de un control colocado sobre una superficie de marca u oscura, donde
      // los fondos de hover claros no tienen contraste. Lo usa la X de cierre de
      // `ActionDialog` con `headerVariant="brand"`.
      onDarkHover: primitives.alpha.white16, // #FFFFFF29
    },

    // ── Bordes ────────────────────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Border / *
    border: {
      subtle: primitives.neutral[250], // #e5e7eb
      default: primitives.neutral[350], // #d1d5db
      strong: primitives.neutral[450], // #9ca3af
      disabled: '#d7d6d6', // inputs Mui deshabilitados
    },

    // ── Feedback: success ─────────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Feedback / Success / *
    success: {
      bg: primitives.green[50], // #E1FAEB
      lighter: primitives.green[300], // #d1fae5
      light: primitives.green[400], // #34d399
      main: primitives.green[500], // #10B981
      dark: primitives.green[600], // #059669
      text: primitives.green[900], // #025832
      icon: primitives.green[700], // #1bb240
    },

    // ── Feedback: error ───────────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Feedback / Error / *
    error: {
      bg: primitives.red[50], // #FEF2F2
      lighter: primitives.red[100], // #fee2e2
      light: primitives.red[200], // #fecaca
      main: primitives.red[500], // #dc2626
      dark: primitives.red[600], // #D32F2F
      text: primitives.red[700], // #C80909
    },

    // ── Feedback: warning ─────────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Feedback / Warning / *
    warning: {
      bg: primitives.orange[50], // #FFF3DB
      lighter: primitives.orange[100], // #FDF5E7
      main: primitives.orange[600], // #C07F0D
      dark: primitives.orange[700], // #FF8C00
      text: primitives.orange[600], // #C07F0D
    },

    // ── Feedback: info ────────────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Feedback / Info / *
    info: {
      bg: primitives.blue[50], // #E6F3FC
      main: primitives.brandPrimary.main, // #0176BC
    },

    // ── Feature: AI assistant ─────────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Feature / AI / *
    ai: {
      primary: primitives.cyan[500], // #0ea5e9 — sky-500
      secondary: primitives.cyan[400], // #06b6d4 — cyan-500
      gradient: 'linear-gradient(135deg, #0EA5E9 0%, #06B6D4 100%)',
    },

    // ── Feature: Question categories ──────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Feature / Category / *
    category: {
      relevant: primitives.teal[500], // #00B8AD
      desired: '#FFC000', // amber-gold
      mandatory: '#CF6159', // red-coral
      ignore: '#D9D9D9', // light grey
    },

    // ── Feature: Stepper / wizard ─────────────────────────────────────────────
    // Nomenclatura Figma: Semantic / Feature / Stepper / *
    stepper: {
      completed: primitives.green[900], // #025832
      completedBg: primitives.green[200], // #CEF1D6
      active: primitives.brandPrimary.main, // #0176BC
      inactive: primitives.neutral[350], // #d1d5db
      connector: primitives.neutral[300], // #D4D4D4
      tableHeader: primitives.blue[50], // #E6F3FC
    },
    // Tabs de seccion (SectionTabs): unico estilo para todo el ATS, sin variantes
    tabs: {
      activeBg: primitives.blue[50], // #E6F3FC
      activeText: primitives.brandPrimary.deep, // #015E96
      inactiveText: primitives.neutral[800], // #262626
    },
  },

  // ── Tipografía ────────────────────────────────────────────────────────────
  typography: {
    fontFamily: {
      primary: 'Poppins, sans-serif',
      secondary: 'Inter, sans-serif',
    },
  },

  // ── Sombras ───────────────────────────────────────────────────────────────
  // Nomenclatura Figma: Semantic / Shadow / *
  shadow: {
    sm: '1px 1px 3px 0px #00000040',
    md: '1px 1px 5px 0px #00000040',
    card: '1px 1px 5px 0px #00000040',
    rating: '2px 2px 10px 0px #B7C6DE',
  },
};

export default tokens
